import { defineRoute } from './defineRoute';

/**
 * 무신사 도메인에서 노출되는 사용자 측 URL.
 * `partner.musinsa.com/curator/...` 프록시가 큐레이터 앱 내부 라우트(29CM 경로와 동일)로 매핑한다.
 *
 * 매핑 정의는 무신사 게이트웨이의 프록시 룰을 그대로 옮긴 것이다.
 * 동일 키의 29CM 값은 paths29CM 참조.
 *
 * 모든 키는 factory 함수. `paths.intro()` 형태로 호출하며 `{ query: {...} }` 옵션 지원.
 */
export const pathsMusinsa = {
  // Group 1
  intro: defineRoute(() => '/curator/intro/pro'),
  join: defineRoute(() => '/curator/join/pro'),
  me: defineRoute(() => '/curator/me/pro'),
  events: defineRoute(() => '/curator/events'),
  terms: defineRoute(() => '/curator/terms'),
  policy: defineRoute(() => '/curator/policy'),
  // 시딩 약관 본문 — desktop 웹에서 "자세히" 를 새 탭으로 연다(모바일/웹뷰는 바텀시트).
  // 기존 /terms·/policy 처럼 약관을 목적별 경로(slug)로 구분. (termId = kebab slug)
  // ⚠️ 무신사 프록시에 `/curator/campaign/seeding/terms/:termId` → `/campaign/seeding/terms/:termId` 매핑 필요(핸드오프).
  seedingTerm: defineRoute(
    ({ termId }: { termId: string }) => `/curator/campaign/seeding/terms/${termId}`,
  ),

  // Group 2
  earningsSummary: defineRoute(() => '/curator/earnings-summary'),
  // 정산정보 등록/변경 라우트 분리. create=미등록(getAccountInfo 미요청)·edit=등록됨(요청). (SID-4631)
  // ⚠️ 무신사 group4 게이트웨이에 /curator/create/account·/curator/edit/account 등록 필요(미등록 시 404). (SID-4631)
  accountCreate: defineRoute(() => '/curator/create/account'),
  accountEdit: defineRoute(() => '/curator/edit/account'),
  salesRanking: defineRoute(() => '/curator/sales-ranking'),
  search: defineRoute(() => '/curator/search'),
  curationInfo: defineRoute(() => '/curator/shop-edit/description'),
  curationOrder: defineRoute(() => '/curator/shop-edit/curation-order'),
  uploadGoods: defineRoute(() => '/curator/upload/goods'),
  uploadSnap: defineRoute(() => '/curator/upload/snap'),
  notFound: defineRoute(() => '/curator/not-found'),
  shop: defineRoute(({ snsTitle }: { snsTitle: string }) => `/curator/s/${snsTitle}`),
  myShop: defineRoute(({ snsTitle }: { snsTitle: string }) => `/curator/myshop/${snsTitle}`),
  editCuration: defineRoute(
    ({ curationId }: { curationId: string }) => `/curator/shop-edit/curation/${curationId}`,
  ),
  snapDetail: defineRoute(
    ({ curationId }: { curationId: string }) => `/curator/snap-detail/${curationId}`,
  ),
  profileSns: defineRoute(
    ({ shopSlug }: { shopSlug: string }) => `/curator/profile-edit/sns/${shopSlug}`,
  ),
  goods: defineRoute(({ hashId }: { hashId: string }) => `/curator/goods/${hashId}`),

  // Group 3
  campaignCollaboration: defineRoute(() => '/curator/campaign/collaboration'),
  campaignProposalDetail: defineRoute(() => '/curator/campaign/proposal/detail'),
  campaignSuggestionDetail: defineRoute(() => '/curator/campaign/suggestion/detail'),
  /** 통일 시딩 상세(참여-후) — campaignId 만으로 진입, type×status 는 화면이 분기. */
  campaignSeedingDetail: defineRoute(
    ({ campaignId }: { campaignId: number | string }) => `/curator/campaign/seeding/${campaignId}`,
  ),
  campaignSearch: defineRoute(() => '/curator/campaign/search'),
  campaignSearchSuggestion: defineRoute(() => '/curator/campaign/search/suggestion'),
  campaignCouponGuide: defineRoute(() => '/curator/campaign/coupon/guide'),
  campaignCouponSettingName: defineRoute(() => '/curator/campaign/coupon/setting-name'),
  shopCoupon: defineRoute(({ shopSlug }: { shopSlug: string }) => `/curator/s/${shopSlug}/coupon`),
  myShopCoupon: defineRoute(
    ({ shopSlug }: { shopSlug: string }) => `/curator/myshop/${shopSlug}/coupon`,
  ),
  shopCouponGoods: defineRoute(
    ({ shopSlug, couponNo }: { shopSlug: string; couponNo: number | string }) =>
      `/curator/s/${shopSlug}/coupons/${couponNo}/goods`,
  ),
  myShopCouponGoods: defineRoute(
    ({ shopSlug, couponNo }: { shopSlug: string; couponNo: number | string }) =>
      `/curator/myshop/${shopSlug}/coupons/${couponNo}/goods`,
  ),

  // 큐레이터 시딩
  settings: defineRoute(() => '/curator/me/settings'),
  // /curator/campaign/seeding 라우트는 server-side redirect 만 유지(북마크 호환).
  // 신규 진입은 campaignCollaboration 사용 → paths factory 키도 제거.
  // 캠페인 신청 목록 (SID-3485) — 큐레이터가 신청 가능한 모집 중 시딩 캠페인 PLP
  campaignRecruitment: defineRoute(() => '/curator/campaign/recruitment'),
  campaignSeeding: defineRoute(() => '/curator/campaign/seeding'),
  // 시딩 캠페인 상세 — kind 별 path 분리. dynamic segment = BE 발급 campaignId.
  // 모집형 상세 3-path 분리 — 29cm 와 동일 구조, /curator prefix 만 추가.
  campaignSeedingRecruitmentDetail: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/recruitment/${campaignId}`,
  ),
  campaignSeedingRecruitmentApplication: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/recruitment/${campaignId}/application`,
  ),
  campaignSeedingRecruitmentProgress: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/recruitment/${campaignId}/progress`,
  ),
  // 제안형 — 수락 전 검토(accept)는 통일 상세(`campaignSeedingDetail`)가 담당, 수락 후 진행만 Progress.
  // (구 campaignSeedingProposalDetail 은 page 부재로 제거 — 통일 상세로 일원화)
  campaignSeedingProposalProgress: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/proposal/${campaignId}/progress`,
  ),
  campaignSeedingRecruitmentContentPeriod: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/recruitment/${campaignId}/content-period`,
  ),
  campaignSeedingProposalContentPeriod: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/proposal/${campaignId}/content-period`,
  ),
  // 콘텐츠 링크 제출 페이지 — 29cm 와 동일 구조, /curator prefix.
  campaignSeedingRecruitmentContentSubmission: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/recruitment/${campaignId}/content-submission`,
  ),
  campaignSeedingProposalContentSubmission: defineRoute(
    ({ campaignId }: { campaignId: number | string }) =>
      `/curator/campaign/seeding/proposal/${campaignId}/content-submission`,
  ),

  // Cross-brand이나 Musinsa는 native deeplink (29CM은 paths29CM.profileEdit가 web route)
  profileEdit: defineRoute(() => '/snap/profile/modify'),

  // 인증 흐름 — 양쪽 환경 동일 path. middleware의 auth error redirect 대상.
  logout: defineRoute(() => '/auth/logout'),
} as const;
